import { FormEvent, useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { getMe, requestProfessionalTier, type MeResponse } from '../api/auth'
import { useAuth } from '../hooks/useAuth'

export default function ProfessionalTierPage() {
  const { token, isAuthenticated } = useAuth()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [details, setDetails] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) return
    getMe(token)
      .then(setMe)
      .catch(() => setLoadError(true))
  }, [token])

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: '/professional-tier' }} replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    setSubmitting(true)
    try {
      const updated = await requestProfessionalTier(token!, details)
      setMe(updated)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit request')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Professional tier</h1>
          <p className="mt-1 text-sm text-gray-500">
            Verified professionals (vets, wildlife officers, biosecurity staff, etc.) carry
            more weight in the fusion model. An admin reviews every request.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-xl">
          {!me && !loadError && (
            <p className="text-sm text-gray-500 text-center">Loading your status…</p>
          )}

          {loadError && (
            <p className="text-sm text-red-600 text-center">Couldn't load your account status.</p>
          )}

          {me && me.credibility_tier === 'verified_professional' && (
            <div className="rounded-lg bg-green-50 border border-green-300 text-green-800 px-4 py-3 text-sm">
              You're already a verified professional on this platform.
            </div>
          )}

          {me && me.credibility_tier !== 'verified_professional' && me.professional_request_status === 'pending' && (
            <div className="rounded-lg bg-blue-50 border border-blue-300 text-blue-800 px-4 py-3 text-sm">
              Your request is submitted and awaiting admin review.
            </div>
          )}

          {me &&
            me.credibility_tier !== 'verified_professional' &&
            me.professional_request_status !== 'pending' && (
              <form onSubmit={handleSubmit} className="space-y-5">
                {me.professional_request_status === 'rejected' && (
                  <div className="rounded-lg bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 text-sm">
                    Your previous request wasn't approved. You can submit a new one below.
                  </div>
                )}

                {submitError && (
                  <div className="rounded-lg bg-red-50 border border-red-300 text-red-700 px-4 py-3 text-sm">
                    {submitError}
                  </div>
                )}

                <div className="space-y-1">
                  <label htmlFor="details" className="block text-sm font-medium text-gray-700">
                    Your professional details
                  </label>
                  <p className="text-xs text-gray-500">
                    e.g. your role, organization, and how it relates to biosecurity or animal
                    health (10–2000 characters).
                  </p>
                  <textarea
                    id="details"
                    required
                    minLength={10}
                    maxLength={2000}
                    rows={5}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    className="w-full rounded-lg bg-gray-100 border border-gray-300 text-gray-900 placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition px-3 py-2"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting || details.trim().length < 10}
                  className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-blue-200 disabled:text-blue-500 text-white text-sm font-semibold py-2.5 transition"
                >
                  {submitting ? 'Submitting…' : 'Submit request'}
                </button>
              </form>
            )}

          <p className="mt-6 text-center text-sm text-gray-500">
            <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
              ← Back to map
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
